import { CreateCardModal } from '@/components/CreateCardModal';
import { Loading } from '@/components/Loading';
import { LinkAccessoryToCardModal } from '@/partials/dashboard/Accessory/LinkAccessoryToCardModal';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import i18n from '@/translations/config';
import { handleError } from '@/utils/handleError';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLink, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

export const LinkAccessory = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isCreateCardModalOpen, setIsCreateCardModalOpen] = useState(false);
  const [isLinkAccessoryToCardModalOpen, setIsLinkAccessoryToCardModalOpen] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { createCard, linkAccessoryToCard, cards, listCards } = useCardStore();
  const { uuid } = useParams<{ uuid: string }>();

  // Animations communes pour les cartes
  const cardAnimation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    whileHover: {
      scale: 1.03,
      boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.1)',
      borderColor: '#0a93e9',
      transition: { duration: 0.2 },
    },
    whileTap: { scale: 0.98 },
  };

  // Animations pour les icônes
  const iconAnimation = {
    initial: { scale: 0 },
    animate: { scale: 1 },
  };

  // Charger les cartes au chargement du composant
  useEffect(() => {
    const fetchCards = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const query = {
          paginate: 0,
          ...(user?.role === 'user' ? { user_id: user.id } : {}),
          ...(user?.role === 'manager' && user.company?.id
            ? { company_id: user.company.id }
            : {}),
        };

        await listCards(query);
      } catch (error) {
        setError(t('linkAccessory:error.loadingCards'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, [listCards, user?.company?.id, user?.id, user?.role, t]);

  // Créer une nouvelle carte et y lier l'accessoire
  const handleCreateCard = useCallback(
    async (userId: number) => {
      if (!uuid) return;

      setIsLoading(true);
      try {
        const newCard = await createCard(userId, uuid);
        if (newCard) {
          navigate(`/${i18n.language}/dashboard/cards/${newCard.slug}`);
        }
      } catch (error) {
        setError(t('linkAccessory:error.creatingCard'));
        setIsLoading(false);
      }
    },
    [createCard, navigate, uuid, t],
  );

  // Lier l'accessoire à une carte existante
  const handleLinkAccessoryToCard = useCallback(
    async (cardId: number) => {
      if (!uuid) return;
      const slug = cards.find(card => card.id === cardId)?.slug;
      if (!slug) return;
      setIsLoading(true);
      try {
        await linkAccessoryToCard(uuid, slug);
        navigate(`/${i18n.language}/dashboard/cards/${slug}`);
      } catch (error) {
        handleError(error, undefined, navigate);
        setError(t('linkAccessory:error.linkingAccessory'));
        setIsLoading(false);
      }
    },
    [linkAccessoryToCard, navigate, uuid, t, cards],
  );

  // Gérer le clic sur l'option "Créer une nouvelle carte"
  const handleCreateCardClick = useCallback(() => {
    if (user?.role === 'user') {
      handleCreateCard(user.id);
    } else {
      setIsCreateCardModalOpen(true);
    }
  }, [user, handleCreateCard]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (localStorage.getItem('from_social_login')) {
    localStorage.removeItem('from_social_login');
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <motion.div
        className="mb-8 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h1
          className="mb-2 bg-gradient-to-r from-linko-dark to-linko-light bg-clip-text text-3xl font-bold text-transparent"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {t('linkAccessory:title')}
        </motion.h1>
        <motion.p
          className="text-gray-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t('linkAccessory:subtitle')}
        </motion.p>
      </motion.div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4 text-center text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Option pour créer une nouvelle carte */}
        <motion.div
          onClick={handleCreateCardClick}
          className="flex cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 p-8 shadow-sm transition-all"
          {...cardAnimation}
          transition={{
            duration: 0.5,
            delay: 0.1,
            type: 'spring' as const,
            stiffness: 100,
            damping: 12,
          }}
        >
          <motion.div
            className="flex size-16 items-center justify-center rounded-full bg-linko-light text-linko-dark"
            {...iconAnimation}
            transition={{
              type: 'spring' as const,
              stiffness: 260,
              damping: 20,
              delay: 0.3,
            }}
          >
            <FaPlus className="size-6" />
          </motion.div>
          <h2 className="text-xl font-semibold text-linko-dark">
            {t('linkAccessory:createNewCard')}
          </h2>
          <p className="text-center text-gray-600">
            {t('linkAccessory:createNewCardDescription')}
          </p>
        </motion.div>

        {/* Option pour lier à une carte existante */}
        {cards.length > 0 ? (
          <motion.div
            onClick={() => setIsLinkAccessoryToCardModalOpen(true)}
            className="flex cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 p-8 shadow-sm transition-all"
            {...cardAnimation}
            transition={{
              duration: 0.5,
              delay: 0.2,
              type: 'spring' as const,
              stiffness: 100,
              damping: 12,
            }}
          >
            <motion.div
              className="flex size-16 items-center justify-center rounded-full bg-linko-light text-linko-dark"
              {...iconAnimation}
              transition={{
                type: 'spring' as const,
                stiffness: 260,
                damping: 20,
                delay: 0.4,
              }}
            >
              <FaLink className="size-6" />
            </motion.div>
            <h2 className="text-xl font-semibold text-linko-dark">
              {t('linkAccessory:linkToExistingCard')}
            </h2>
            <p className="text-center text-gray-600">
              {t('linkAccessory:linkToExistingCardDescription')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            className="flex flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-8 opacity-70"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{
              duration: 0.5,
              delay: 0.2,
              type: 'spring' as const,
              stiffness: 100,
              damping: 12,
            }}
          >
            <motion.div
              className="flex size-16 items-center justify-center rounded-full bg-gray-200 text-gray-400"
              {...iconAnimation}
              transition={{
                type: 'spring' as const,
                stiffness: 260,
                damping: 20,
                delay: 0.4,
              }}
            >
              <FaLink className="size-6" />
            </motion.div>
            <h2 className="text-xl font-semibold text-gray-500">
              {t('linkAccessory:linkToExistingCard')}
            </h2>
            <p className="text-center text-gray-500">
              {t('linkAccessory:noExistingCards')}
            </p>
          </motion.div>
        )}
      </div>

      {/* Modals */}
      <CreateCardModal
        isOpen={isCreateCardModalOpen}
        onClose={() => setIsCreateCardModalOpen(false)}
        onCreateForSelf={() => handleCreateCard(user?.id as number)}
        onCreateForUser={handleCreateCard}
      />
      <LinkAccessoryToCardModal
        isOpen={isLinkAccessoryToCardModalOpen}
        onClose={() => setIsLinkAccessoryToCardModalOpen(false)}
        onLinkToCard={handleLinkAccessoryToCard}
      />
    </div>
  );
};
